import { useEffect, useRef, useState } from 'react';

// Confirm step for deleting a client scan record (TYR-219). A native <dialog>
// opened with showModal(), so the browser gives the focus trap, the Escape
// close and the focus return to the button that opened it. The confirm stays
// disabled until the owner types the record's own name back, which is the
// point: this is the one action in /internal that destroys client data.
export type DeleteRecordDialogProps = {
  open: boolean;
  // How the record is named in the sentences, and the word that has to be
  // typed. They are the company, except on a record that has none.
  client: string;
  confirmWord: string;
  approved: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

export function DeleteRecordDialog({
  open,
  client,
  confirmWord,
  approved,
  busy,
  error,
  onConfirm,
  onCancel,
}: DeleteRecordDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      setTyped('');
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  const armed = typed.trim() === confirmWord && !busy;

  return (
    <dialog
      ref={ref}
      // Fires on Escape and on close(): one path back to the parent's state,
      // so the dialog and the page can never disagree about being open.
      onClose={onCancel}
      className="w-[92vw] max-w-lg bg-surface-1 text-crux-light border border-border p-5 backdrop:bg-black/50"
    >
      <h2 className="text-lg font-semibold">Delete the scan record for {client}?</h2>
      <div className="mt-3 space-y-2 text-sm text-neutral-400">
        {approved ? (
          <>
            <p>
              This deletes the scan results, the saved AI reports, the invite and revisit links,
              and the name and company on the invite.
            </p>
            <p>
              The approval record stays, because /privacy says we keep it as evidence that the
              scan was authorised: terms version, time and IP address, the email the invite was
              sent to, and the Salesforce username and org ID of the approver. This record can
              never be scanned again, but the address is free: invite it again and it starts a
              new record.
            </p>
          </>
        ) : (
          <p>
            This record was never approved, so everything goes: the record itself, the scan
            results, the saved AI reports, the invite and revisit links, and the contact details.
            Nothing is kept.
          </p>
        )}
        <p>
          The scan results email already sent to TYRL at scan time cannot be recalled. This
          cannot be undone.
        </p>
      </div>

      <label className="block mt-4 text-xs text-neutral-500">
        Type <span className="font-mono text-crux-light">{confirmWord}</span> to confirm.
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          className="mt-1.5 w-full h-9 bg-surface-3 border border-border px-3 text-[13px] text-crux-light outline-none focus:border-sunset"
        />
      </label>

      {error ? <p className="mt-3 text-sm text-sunset">{error}</p> : null}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 border border-border text-sm hover:bg-surface-2"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!armed}
          className="px-3 py-1.5 border border-sunset/50 bg-sunset/15 text-sm hover:bg-sunset/25 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete record'}
        </button>
      </div>
    </dialog>
  );
}
